import { useCallback, useEffect, useRef, useState, type ChangeEvent, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlacesAutocomplete, type PlacesSelection } from '@/hooks/usePlacesAutocomplete';
import { RecaptchaNotice } from '@/components/RecaptchaNotice';
import { WCta } from '../WCta';
import { WregPhase } from '../WregPhase';
import { WREG_WORKING_MS } from '../wregSteps';
import {
  EMPTY_SHIPPING,
  normalizeZip,
  shippingIsComplete,
  type ShippingForm,
} from './wregShippingFields';

type Props = {
  initial?: ShippingForm;
  onContinue: (form: ShippingForm) => void | Promise<void>;
  onContinued?: () => void;
};

const WregMonitor = ({ initial, onContinue, onContinued }: Props) => {
  const { t } = useTranslation();
  const [f, setF] = useState<ShippingForm>(() => initial ?? EMPTY_SHIPPING);
  const [bfade, setBfade] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const streetRef = useRef<HTMLInputElement>(null);

  const fadeFor = (el: HTMLElement) => el.scrollTop + el.clientHeight < el.scrollHeight - 4;
  const onScroll = (e: UIEvent<HTMLDivElement>) => setBfade(fadeFor(e.currentTarget));

  useEffect(() => {
    const el = bodyRef.current;
    if (el) setBfade(fadeFor(el));
  }, []);

  const set = (k: keyof ShippingForm) => (e: ChangeEvent<HTMLInputElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));

  const setZip = (e: ChangeEvent<HTMLInputElement>) =>
    setF((p) => ({ ...p, zip: normalizeZip(e.target.value) }));

  const applyPlace = useCallback((place: PlacesSelection) => {
    setF((p) => ({
      ...p,
      street: place.street || p.street,
      city: place.city || p.city,
      state: place.state || p.state,
      zip: place.zip ? normalizeZip(place.zip) : p.zip,
    }));
  }, []);

  const { enabled: placesEnabled } = usePlacesAutocomplete({
    inputRef: streetRef,
    onSelect: applyPlace,
    scrollRef: bodyRef,
    placeholder: t('wreg.mon.street.ph'),
  });

  const valid = shippingIsComplete(f);

  return (
    <div className="rg-screen">
      <div className="rg-body-wrap">
        <div className="rg-body" ref={bodyRef} onScroll={onScroll}>
          <div className="rg-inner">
            <div className="rg-head-block">
              <WregPhase n={4} />
              <h1 className="rg-h1">{t('wreg.mon.title')}</h1>
              <p className="rg-sub">{t('wreg.mon.sub')}</p>
            </div>
            <div className="rg-ship-head">{t('wreg.mon.ship_head')}</div>
            <div className="rg-field-row">
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-mon-first">
                  {t('wreg.intake.first')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-mon-first"
                    className="rg-input"
                    autoComplete="given-name"
                    value={f.first}
                    onChange={set('first')}
                  />
                </div>
              </div>
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-mon-last">
                  {t('wreg.intake.last')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-mon-last"
                    className="rg-input"
                    autoComplete="family-name"
                    value={f.last}
                    onChange={set('last')}
                  />
                </div>
              </div>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-mon-street">
                {t('wreg.mon.street.label')}
              </label>
              <div className="rg-input-wrap">
                <input
                  id="wreg-mon-street"
                  ref={streetRef}
                  className="rg-input"
                  value={f.street}
                  onChange={set('street')}
                  placeholder={t('wreg.mon.street.ph')}
                  autoComplete={placesEnabled ? 'off' : 'address-line1'}
                />
              </div>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-mon-apt">
                {t('wreg.mon.apt.label')}{' '}
                <span className="rg-optional">{t('wreg.mon.optional')}</span>
              </label>
              <div className="rg-input-wrap">
                <input
                  id="wreg-mon-apt"
                  className="rg-input"
                  autoComplete="address-line2"
                  value={f.apt}
                  onChange={set('apt')}
                  placeholder={t('wreg.mon.apt.ph')}
                />
              </div>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-mon-city">
                {t('wreg.mon.city')}
              </label>
              <div className="rg-input-wrap">
                <input
                  id="wreg-mon-city"
                  className="rg-input"
                  autoComplete="address-level2"
                  value={f.city}
                  onChange={set('city')}
                />
              </div>
            </div>
            <div className="rg-field-row">
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-mon-state">
                  {t('wreg.mon.state')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-mon-state"
                    className="rg-input"
                    autoComplete="address-level1"
                    value={f.state}
                    onChange={set('state')}
                  />
                </div>
              </div>
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-mon-zip">
                  {t('wreg.mon.zip')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-mon-zip"
                    className="rg-input"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    value={f.zip}
                    onChange={setZip}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={`rg-list-fade${bfade ? ' show' : ''}`} aria-hidden="true" />
      </div>
      <div className="rg-foot">
        <div className="wreg-foot-row">
          <WCta
            disabled={!valid}
            working={t('wreg.mon.cta_loading')}
            minWorkingMs={WREG_WORKING_MS}
            onClick={() => onContinue(f)}
            onDone={onContinued}
          >
            {t('wreg.mon.cta')}
          </WCta>
          {!valid && <span className="wreg-foot-tip">{t('wreg.foot.fillall')}</span>}
        </div>
        <div className="wreg-foot-legal">
          <RecaptchaNotice />
        </div>
      </div>
    </div>
  );
};

export { WregMonitor };
